import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiDownload, FiRefreshCw, FiDollarSign, FiFileText } from 'react-icons/fi';
import { FaUserCircle } from 'react-icons/fa';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../util/SpinnerCargando';
import InputClientes from '../util/Inputs/InputClientes';
import PlantillaModal from '../util/PlantillaModal';
import TablaEntidad from '../util/tablas/TablaEntidad';
import ModalAplicarPago from './ModalAplicarPago';
import generarPDFEstadoCuentaCliente from '../reportes/generarPDFEstadoCuentaCliente';
import generarExcelEstadoCuentaCliente from '../reportes/generarExcelEstadoCuentaCliente';

function formatMonto(monto) {
  return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(fecha) {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
}

const ETIQUETAS_TIPO = { factura: 'Factura', pago: 'Pago', nota_credito: 'Nota de Crédito' };

const ESTILO_ESTADO = {
  pagada: 'bg-green-100 text-green-700',
  parcial: 'bg-yellow-100 text-yellow-700',
  pendiente: 'bg-blue-100 text-blue-700',
  vencida: 'bg-red-100 text-red-700'
};

function TarjetaMetrica({ titulo, valor, colorBorde, colorTexto }) {
  return (
    <div className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde}`}>
      <p className="text-sm text-gray-600 font-medium mb-1">{titulo}</p>
      <p className={`text-xl font-bold ${colorTexto}`}>{valor}</p>
    </div>
  );
}

const ENCABEZADO = 'px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider';
const INPUT = 'w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors';

// =====================================================
// PANTALLA: Perfil de cliente
// =====================================================
export default function PerfilCliente() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [mostrarModalClientes, setMostrarModalClientes] = useState(false);
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  const [pestana, setPestana] = useState('facturas');
  const [filtros, setFiltros] = useState({ fecha_desde: '', fecha_hasta: '' });
  const [periodo, setPeriodo] = useState(null);

  const idCliente = searchParams.get('cliente') || '';

  const cargarDatos = useCallback(async () => {
    if (!idCliente) { setDatos(null); return; }
    try {
      setCargando(true);
      const { data } = await instanciaAxios.get(`/cxc/cliente/${idCliente}/estado-cuenta`);
      setDatos(data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar el perfil del cliente:', error);
      toast.error('Error al cargar el perfil del cliente');
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, [idCliente]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // Al cambiar de cliente se limpian las fechas
  useEffect(() => { setFiltros({ fecha_desde: '', fecha_hasta: '' }); }, [idCliente]);

  const hayFiltro = Boolean(filtros.fecha_desde || filtros.fecha_hasta);

  // Con fechas se pide el estado de cuenta del periodo; sin fechas se usa el historial completo.
  // El pago siempre se reparte sobre el historial completo (datos), nunca sobre el periodo.
  const cargarPeriodo = useCallback(async () => {
    if (!idCliente || !hayFiltro) { setPeriodo(null); return; }
    if (filtros.fecha_desde && filtros.fecha_hasta && filtros.fecha_desde > filtros.fecha_hasta) {
      toast.error('La fecha "Desde" no puede ser posterior a "Hasta"');
      return;
    }
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== ''));
      const { data } = await instanciaAxios.get(`/cxc/cliente/${idCliente}/estado-cuenta`, { params });
      setPeriodo(data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cargar el periodo seleccionado');
    }
  }, [idCliente, filtros, hayFiltro]);

  useEffect(() => { cargarPeriodo(); }, [cargarPeriodo]);

  const vista = periodo || datos;

  const recargar = () => { cargarDatos(); cargarPeriodo(); };

  const handleClienteSeleccionado = (entidad) => {
    setMostrarModalClientes(false);
    if (entidad) setSearchParams({ cliente: String(entidad.id_entidad) });
  };

  // Facturas con deuda, de la mas vieja a la mas nueva (orden en que se aplicaran los pagos)
  const pendientes = useMemo(() => {
    if (!datos) return [];
    return datos.facturas
      .filter(f => f.balance > 0)
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha) || a.id_factura - b.id_factura);
  }, [datos]);

  const exportar = async (generar, etiqueta) => {
    try {
      await generar(vista);
    } catch (error) {
      console.error(`Error al generar el ${etiqueta}:`, error);
      toast.error(`Error al generar el ${etiqueta}`);
    }
  };

  const estadoFactura = (f) => (f.estado_vencimiento === 'vencida' ? 'vencida' : f.estado_pago);

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-8">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-8 w-full max-w-7xl mx-6">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaUserCircle className="text-color-primario text-3xl" />
                <h1 className="font-cascadia font-bold text-2xl">Cobros</h1>
              </div>
              <button onClick={recargar} disabled={cargando || !idCliente}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors" title="Actualizar">
                <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-300 shadow-md p-4">
              <label className="block text-xs text-gray-500 mb-1">Cliente</label>
              <InputClientes
                onClick={() => setMostrarModalClientes(true)}
                valor={datos?.cliente?.id_entidad_cliente || idCliente}
                nombreCliente={datos?.cliente?.razon_social || ''}
                placeholder="Buscar cliente..."
                editable={true}
              />
            </div>

            {cargando && <SpinnerCargando />}

            {!cargando && !datos && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-12 text-center">
                <p className="text-gray-500 text-lg">Seleccione un cliente para ver su perfil</p>
              </div>
            )}

            {!cargando && datos && (
              <>
                <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6 flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-cascadia text-lg font-bold text-gray-800 mb-2">{datos.cliente.razon_social}</h2>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                      {datos.cliente.rnc && <p>RNC/Cédula: {datos.cliente.rnc}</p>}
                      {datos.cliente.telefono && <p>Teléfono: {datos.cliente.telefono}</p>}
                      {datos.cliente.email && <p>Email: {datos.cliente.email}</p>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button onClick={() => setMostrarModalPago(true)} disabled={pendientes.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={pendientes.length === 0 ? 'El cliente no tiene facturas pendientes' : 'Aplicar pago a la cuenta'}>
                      <FiDollarSign size={18} /> Aplicar pago
                    </button>
                    <button onClick={() => exportar(generarPDFEstadoCuentaCliente, 'PDF')}
                      className="flex items-center gap-2 px-4 py-2 bg-color-secundario text-white rounded-xl hover:bg-opacity-90">
                      <FiDownload size={18} /> Estado de cuenta PDF
                    </button>
                    <button onClick={() => exportar(generarExcelEstadoCuentaCliente, 'Excel')}
                      className="flex items-center gap-2 px-4 py-2 border border-color-secundario text-color-secundario rounded-xl hover:bg-gray-100">
                      <FiFileText size={18} /> Excel
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-300 shadow-md p-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Desde</label>
                      <input type="date" value={filtros.fecha_desde} max={filtros.fecha_hasta || undefined}
                        onChange={(e) => setFiltros(prev => ({ ...prev, fecha_desde: e.target.value }))} className={INPUT} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                      <input type="date" value={filtros.fecha_hasta} min={filtros.fecha_desde || undefined}
                        onChange={(e) => setFiltros(prev => ({ ...prev, fecha_hasta: e.target.value }))} className={INPUT} />
                    </div>
                    {hayFiltro && (
                      <button onClick={() => setFiltros({ fecha_desde: '', fecha_hasta: '' })}
                        className="px-3 py-2 text-sm border border-gray-400 rounded-lg hover:bg-gray-100">
                        Quitar filtro
                      </button>
                    )}
                  </div>
                  {hayFiltro && (
                    <p className="text-xs text-gray-500 mt-2">
                      Las tarjetas, pestañas y exportaciones muestran solo el periodo seleccionado. El balance corrido del
                      estado de cuenta parte de cero al inicio del periodo. Aplicar pago siempre usa todas las facturas pendientes.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <TarjetaMetrica titulo="Total Facturado" valor={`RD$ ${formatMonto(vista.resumen.total_facturado)}`} colorBorde="border-blue-500" colorTexto="text-blue-700" />
                  <TarjetaMetrica titulo="Total Pagado" valor={`RD$ ${formatMonto(vista.resumen.total_pagado)}`} colorBorde="border-green-500" colorTexto="text-green-700" />
                  <TarjetaMetrica titulo="Balance Pendiente" valor={`RD$ ${formatMonto(vista.resumen.total_pendiente)}`} colorBorde="border-orange-500" colorTexto="text-orange-700" />
                  <TarjetaMetrica titulo="Balance Vencido" valor={`RD$ ${formatMonto(vista.resumen.total_vencido)}`} colorBorde="border-red-500" colorTexto="text-red-700" />
                </div>

                <div className="flex gap-2 border-b border-gray-300">
                  {[['facturas', `Facturas (${vista.facturas.length})`], ['movimientos', 'Estado de cuenta'], ['antiguedad', 'Antigüedad']].map(([id, label]) => (
                    <button key={id} onClick={() => setPestana(id)}
                      className={`px-4 py-2 font-medium border-b-2 -mb-px transition-colors ${
                        pestana === id ? 'border-color-primario text-color-primario' : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>

                <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6 overflow-x-auto">
                  {pestana === 'facturas' && (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={`${ENCABEZADO} text-left`}>Factura</th>
                          <th className={`${ENCABEZADO} text-left`}>Fecha</th>
                          <th className={`${ENCABEZADO} text-left`}>Vence</th>
                          <th className={`${ENCABEZADO} text-right`}>Total</th>
                          <th className={`${ENCABEZADO} text-right`}>Balance</th>
                          <th className={`${ENCABEZADO} text-center`}>Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {[...vista.facturas].reverse().map(f => (
                          <tr key={f.id_factura} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{f.ncf || `Factura #${f.id_factura}`}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatFecha(f.fecha)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatFecha(f.fecha_vencimiento)}</td>
                            <td className="px-4 py-3 text-sm text-right">RD$ {formatMonto(f.monto_total)}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">RD$ {formatMonto(f.balance)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${ESTILO_ESTADO[estadoFactura(f)]}`}>
                                {estadoFactura(f)}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {vista.facturas.length === 0 && (
                          <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">El cliente no tiene facturas</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {pestana === 'movimientos' && (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={`${ENCABEZADO} text-left`}>Fecha</th>
                          <th className={`${ENCABEZADO} text-left`}>Tipo</th>
                          <th className={`${ENCABEZADO} text-left`}>Documento</th>
                          <th className={`${ENCABEZADO} text-right`}>Cargo</th>
                          <th className={`${ENCABEZADO} text-right`}>Abono</th>
                          <th className={`${ENCABEZADO} text-right`}>Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {vista.movimientos.map((m, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600">{formatFecha(m.fecha)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{ETIQUETAS_TIPO[m.tipo] || m.tipo}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.documento}</td>
                            <td className="px-4 py-3 text-sm text-right">{m.cargo > 0 ? `RD$ ${formatMonto(m.cargo)}` : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right">{m.abono > 0 ? `RD$ ${formatMonto(m.abono)}` : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">RD$ {formatMonto(m.balance)}</td>
                          </tr>
                        ))}
                        {vista.movimientos.length === 0 && (
                          <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Sin movimientos</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {pestana === 'antiguedad' && (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={`${ENCABEZADO} text-left`}>Rango</th>
                          <th className={`${ENCABEZADO} text-center`}>Facturas</th>
                          <th className={`${ENCABEZADO} text-right`}>Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {vista.antiguedad_cartera.map((a, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3 text-sm font-medium capitalize">{a.rango}</td>
                            <td className="px-4 py-3 text-sm text-center">{a.cantidad}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">RD$ {formatMonto(a.total)}</td>
                          </tr>
                        ))}
                        {vista.antiguedad_cartera.length === 0 && (
                          <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500">Sin saldos pendientes</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <PlantillaModal mostrar={mostrarModalClientes} onClose={() => setMostrarModalClientes(false)} titulo="Seleccionar Cliente">
        <TablaEntidad tipoEntidad={1} descripcion="Seleccione un cliente" setEntidadSeleccionada={handleClienteSeleccionado} />
      </PlantillaModal>

      {datos && (
        <ModalAplicarPago
          mostrar={mostrarModalPago}
          onClose={() => setMostrarModalPago(false)}
          cliente={datos.cliente}
          onPagoAplicado={recargar}
        />
      )}
    </div>
  );
}
